import type { SearchStreamEvent } from './types';
import type { QueueContentTypes } from './types';

export interface SearchStreamParams {
  query: string;
  context?: string[];
  resumeText?: string | null;
  /** 'simple' routes to the lean jobs-first deck pipeline; defaults to unified. */
  mode?: 'simple' | 'unified';
  /** Content-lane scope for the simple pipeline. Omit = DEFAULT_QUEUE_CONTENT_TYPES (jobs-only). */
  contentTypes?: QueueContentTypes;
}

/**
 * SSE-over-fetch transport for the unified-search stream.
 *
 * Opens a POST to /api/search/stream, parses the `data: <json>` server-sent
 * events, and forwards each parsed event to `onEvent`. Transport-level
 * failures (non-OK response, missing body, network error) surface through
 * `onEvent` as `{ type: 'error' }`. Reading stops as soon as a `{ type: 'done' }`
 * event arrives. Returns an abort function synchronously — the public API is
 * intentionally NOT async.
 */
export function createSearchStream(
  params: SearchStreamParams,
  onEvent: (event: SearchStreamEvent) => void,
): () => void {
  const controller = new AbortController();

  void (async () => {
    try {
      const { query, context, resumeText, mode, contentTypes } = params;
      const fullDescription = context?.length
        ? [...context, query].join('. ')
        : query;
      const response = await fetch('/api/search/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: fullDescription, query, context, resumeText: resumeText || undefined, mode, contentTypes }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        onEvent({ type: 'error', message: 'Search request failed' });
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let sawTerminal = false;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let separatorIndex = buffer.indexOf('\n\n');
        while (separatorIndex !== -1) {
          const rawEvent = buffer.slice(0, separatorIndex);
          buffer = buffer.slice(separatorIndex + 2);
          separatorIndex = buffer.indexOf('\n\n');

          const dataLines = rawEvent
            .split('\n')
            .filter((line) => line.startsWith('data: '))
            .map((line) => line.slice(6));
          if (dataLines.length === 0) continue;

          let event: SearchStreamEvent | null = null;
          try {
            event = JSON.parse(dataLines.join('\n')) as SearchStreamEvent;
          } catch {
            continue;
          }

          if (!event) continue;

          onEvent(event);

          if (event.type === 'done') {
            return;
          }
          if (event.type === 'error') {
            sawTerminal = true;
          }
        }
      }

      // Clean EOF with no terminal `done`/`error` frame (e.g. an LB/proxy
      // idle-timeout half-close on a long search, or a corrupted `done`
      // chunk). Consumers clear their streaming state only on a terminal
      // event, so synthesize a `done` here to avoid a stuck "searching"
      // dead-end. Skipped on abort (which surfaces as a thrown error below).
      if (!sawTerminal && !controller.signal.aborted) {
        onEvent({ type: 'done' });
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error('Search stream error:', error);
      onEvent({ type: 'error', message: error instanceof Error ? error.message : 'Search stream error' });
    }
  })();

  return () => controller.abort();
}
